import React, { useEffect, useMemo, useState } from 'react';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ChartOptions,
  registerables,
} from 'chart.js';
import DashboardBigCard from '../dashboardCards/dashboardBigCard';
import { useGetMonthlyDirectFormSubmissionHistogram } from '../../../apiHooks/queries/graphs/useGetMonthlyDirectFormSubmissionHistogram';
import { MONTHLY_DIRECT_AD_HISTOGRAM } from '../../../constants/pageIdentifiers';
import { useDashboardFilterDateStore } from '../../../store/dateRangeStore';
import { formatNumber } from '../../../constants/constants';
import {useCustomFieldsStore} from "../../../store/customFieldsStore";
ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, ...registerables);

const colors = ['#94BFA7', '#E98985', '#FFC577'];

interface MonthlyDirectFormSubmissionHistogramData {
  data: {
    labels: string[];
    dataSets: Record<string, number[]>;
  };
}

interface Visibility {
  earned: boolean;
  lostIncome: boolean;
  potentialIncome: boolean;
}

const buildData = (
  monthlyDirectFormSubmissionHistogramData: MonthlyDirectFormSubmissionHistogramData | undefined,
  t: (_key: string) => string,
  visibility: Visibility,
) => ({
  labels: monthlyDirectFormSubmissionHistogramData?.data?.labels.map((label: string) => label.slice(0, 3)) || [],
  datasets: Object.entries(monthlyDirectFormSubmissionHistogramData?.data?.dataSets || {})
    .map(([key, value], index) => ({
      label: t(key),
      data: value.map((v: number) => (visibility[key as keyof Visibility] ? v : 0)),
      fill: true,
      backgroundColor: colors[index % colors.length],
      borderRadius: 5,
      borderWidth: 1,
    })),
});

const options: ChartOptions<'bar'> = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      display: false,
    },
    title: {
      display: false,
    },
    tooltip: {
      mode: 'index',
      intersect: false,
    },
  },
  scales: {
    x: {
      beginAtZero: true,
      grid: {
        display: false,
      },
      ticks: {
        font: {
          family: 'Poppins',
        },
      },
    },
    y: {
      beginAtZero: true,
      ticks: {
        display: true,
        callback(value) {
          return formatNumber(Number(value));
        },
        font: {
          family: 'Poppins',
        },
      },
      border: {
        display: false,
        dash: [2, 2],
        color: '#e0e0e0',
      },
    },
  },
  layout: {
    padding: {
      top: 20,
      left: 20,
      right: 20,
      bottom: 20,
    },
  },
};

const initialVisibility: Visibility = {
  earned: true,
  lostIncome: true,
  potentialIncome: true,
};

const MonthlyDirectFormSubmissionHistogram = () => {
  const { t } = useTranslation();
  const { dashboardFilterDateRange } = useDashboardFilterDateStore();
  const { selectedCustomField } = useCustomFieldsStore();

  const [visibility, setVisibility] = useState<Visibility>(initialVisibility);

  const {
    data: histogramData,
    isError,
    isLoading,
  } = useGetMonthlyDirectFormSubmissionHistogram(
      MONTHLY_DIRECT_AD_HISTOGRAM,
      dashboardFilterDateRange[0],
      dashboardFilterDateRange[1],
      selectedCustomField?.id,
      selectedCustomField?.name,
  );

  const hasMoreThan12Months = histogramData?.data?.labels?.length > 11;

  const toggleDataset = (datasetLabel: keyof Visibility) => {
    setVisibility((prev) => ({
      ...prev,
      [datasetLabel]: !prev[datasetLabel],
    }));
  };

  return (
    <DashboardBigCard
      info="See and compare monthly how much revenue your organic traffic brings in."
      title="Organic Performance"
      isLoading={isLoading}
      isError={isError}
      hasMoreThan12Months={hasMoreThan12Months}
    >
      {histogramData?.data?.dataSets && !isError && !isLoading && (
        <div className="mb-4 h-full max-h-[400px] w-full">
          <div className="flex w-full min-w-[160px] justify-center gap-2 pb-4">
            {Object.keys(initialVisibility).map((key) => (
              <button
                type="button"
                key={key}
                onClick={() => toggleDataset(key as keyof Visibility)}
                className={`flex h-[35px] w-full min-w-[130px] items-center gap-2 rounded-xl 
                  border py-2 pl-4 pr-2 text-[10px] outline-none focus:outline-none ${
                            visibility[key as keyof Visibility] ? 'bg-white text-primary' : 'text-black'
                        }`}
              >
                <div
                  className="h-2 w-2 rounded-full"
                  style={{
                    backgroundColor: visibility[key as keyof Visibility]
                      ? colors[Object.keys(initialVisibility).indexOf(key) % colors.length]
                      : 'gray',
                  }}
                />
                {t(key)}
              </button>
            ))}
          </div>
          <div className="flex h-full w-full justify-center">
            <Bar options={options} data={buildData(histogramData, t, visibility)} />
          </div>
        </div>
      )}
    </DashboardBigCard>
  );
};

export default MonthlyDirectFormSubmissionHistogram;
